<template>
  <div class="checkers-container">
    <!-- 1) Tela de criação de sessão -->
    <div v-if="!sessionCreated" class="initial-screen">
      <h1>Damas Multiplayer</h1>
      <div class="bet-area">
        <label>Valor da aposta:</label>
        <select v-model.number="betAmount">
          <option v-for="v in betOptions" :key="v" :value="v">{{ v }}</option>
        </select>
        <span class="balance">
          {{ userName }}: {{ userBalance }} <strong>Zephyr Points</strong>
        </span>
      </div>
      <div class="start-menu">
        <button class="btn" @click="createSession(true)">
          Partida Pública
        </button>
        <button class="btn" @click="createSession(false)">
          Partida Privada
        </button>
      </div>
    </div>

    <!-- 2) Lobby de espera -->
    <div v-else-if="!gameStarted" class="initial-screen">
      <p><strong>Link da sala:</strong>
        <a :href="sessionLink" target="_blank">{{ sessionLink }}</a>
      </p>
      <div v-if="isPrivate">
        <p><strong>Senha:</strong> {{ sessionPassword }}</p>
      </div>
      <p>Aguardando jogadores: {{ players.length }}/2</p>
      <ul>
        <li v-for="p in players" :key="p">{{ p }}</li>
      </ul>
    </div>

    <!-- 3) Área de jogo -->
    <div v-else class="game-area">
      <h1>Damas Multiplayer</h1>
      <p class="info-line">Aposta: {{ betAmount }} Zephyr Points</p>
      <p class="info-line">
        Turno de: <strong>{{ players[currentPlayer] }}</strong>
        <span v-if="isMyTurn"> (É sua vez!)</span>
      </p>

      <div class="board">
        <div
          v-for="(row, r) in displayBoard"
          :key="r"
          class="row"
        >
          <div
            v-for="(_, c) in row"
            :key="c"
            class="cell"
            :class="{
              dark: (r + c) % 2 === 1,
              selected: selectedDisplay && selectedDisplay.r === r && selectedDisplay.c === c
            }"
            @click="onCellClick(r, c)"
          >
            <div
              v-if="displayBoard[r][c]"
              class="piece"
              :class="{
                me: displayBoard[r][c].mid === userName,
                opp: displayBoard[r][c].mid !== userName,
                king: displayBoard[r][c].king
              }"
            ></div>
          </div>
        </div>
      </div>

      <div class="host-controls" v-if="isHost && gameOver">
        <button class="btn" @click="startNewGame">Reiniciar partida</button>
        <button class="btn" @click="leaveRoom">Sair da sala</button>
      </div>
      <button v-if="!isHost && gameOver" class="btn" @click="leaveRoom">
        Sair da sala
      </button>
    </div>
        <!-- popup de erro / inválido -->
            <div
            v-if="errorMsg"
            class="popup error-popup"
            @click="errorMsg = ''"
            >
            {{ errorMsg }}
            </div>

            <!-- popup de vitória -->
             <div v-if="gameOver" class="popup victory-popup">
                <template v-if="status.reason === 'left' && winner === userName">
                <p>⚠️ O jogador {{ status.leftMid }} saiu da partida!</p>
                <p>Você venceu por abandono e ganhou {{ betAmount * 3 }} Zephyr Points!</p>
                </template>
                <template v-else-if="winner === userName">
                <p>🎉 Você venceu!</p>
                <p>Ganhou {{ betAmount * 3 }} Zephyr Points!</p>
                </template>
                <template v-else>
                <p>🏆 {{ winner }} venceu e ganhou {{ betAmount * 3 }} Zephyr Points!</p>
                </template>
                <button class="btn" @click="leaveRoom">Fechar</button>
                <!-- Abandono ou timeout -->
                <div v-if="status.reason === 'left' || status.reason === 'timeout'"
                    class="popup abandon-popup">
                    <p v-if="status.reason === 'left'">
                    ⚠️ O jogador {{ status.leftMid }} saiu da partida!
                    </p>
                    <p v-else>
                    ⏱️ O jogador {{ status.leftMid }} desconectou (timeout)!
                    </p>
                    <p>Você venceu e ganhou {{ betAmount * 2 }} Zephyr Points!</p>
                    <button class="btn" @click="leaveRoom">Fechar</button>
                </div>
            </div>
  </div>
  
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  fetchMe, fetchBetBalance,
  createCheckersSession,
  joinCheckersSession,
  fetchCheckersSessionStatus,
  actionCheckersSession,
  restartCheckersSession,
  leaveCheckersSession,
  pingCheckersSession
} from '@/services/api';

const route = useRoute(), router = useRouter();
const token = localStorage.getItem('jwt_token');

const userName    = ref('');
const userBalance = ref(0);
const betOptions  = [100,200,300,500,700,1000];
const betAmount   = ref(100);

const sessionCreated  = ref(false);
const isPrivate       = ref(false);
const isHost          = ref(false);
const sessionLink     = ref('');
const sessionPassword = ref('');
const sessionId       = ref('');
const players         = ref([]);
const board           = ref([]);
const gameStarted     = ref(false);
const gameOver        = ref(false);
const currentPlayer   = ref(0);
const winner          = ref(null);
const selectedModel   = ref(null);


const localIdx  = computed(() => players.value.indexOf(userName.value));
const flipBoard = computed(() => localIdx.value === 0);

// reorganiza o tabuleiro para exibição
const displayBoard = computed(() => {
  if (!board.value) return [];
  if (!flipBoard.value) return board.value;
  // inverte linhas e colunas
  return board.value
    .map(row => [...row].reverse())
    .reverse();
});

let pingInterval = null;
onMounted(() => {
  // ... seu join/pollStatus
  pingInterval = setInterval(() => {
    pingCheckersSession(sessionId.value, token)
      .catch(() => {}); // ignora erros
  }, 5000);
});
onBeforeUnmount(() => {
  if (pingInterval) clearInterval(pingInterval);
});
// exibe seleção no tabuleiro
const selectedDisplay = computed(() => {
  if (!selectedModel.value) return null;
  const { r, c } = selectedModel.value;
  if (!flipBoard.value) return { r, c };
  return { r: 7 - r, c: 7 - c };
});

const isMyTurn = computed(() =>
  gameStarted.value &&
  !gameOver.value &&
  currentPlayer.value === localIdx.value
);
const errorMsg = ref('');
// helper local para verificar se uma peça ainda pode capturar
function hasCaptureMovesLocal(board, r, c, players) {
  const piece = board[r][c];
  if (!piece) return false;
  const idx     = players.indexOf(piece.mid);
  const forward = idx === 0 ? 1 : -1;
  const dirs    = piece.king
    ? [[1,1],[1,-1],[-1,1],[-1,-1]]
    : [[forward,1],[forward,-1]];

  for (const [dr,dc] of dirs) {
    const midR = r + dr, midC = c + dc;
    const toR  = r + 2*dr, toC  = c + 2*dc;
    if (
      midR >= 0 && midR < 8 && midC >= 0 && midC < 8 &&
      toR  >= 0 && toR  < 8 && toC  >= 0 && toC  < 8 &&
      board[midR][midC]?.mid !== piece.mid &&
      board[toR][toC] == null
    ) {
      return true;
    }
  }
  return false;
}

// exibe popup de erro
function showError(msg) {
  errorMsg.value = msg;
  setTimeout(() => (errorMsg.value = ''), 2500);
}
// ao montar, busca usuário e saldo
onMounted(async () => {
  if (token) {
    try {
      const me  = await fetchMe(token);
      userName.value = me.user.mid;
      const bal = await fetchBetBalance(token);
      userBalance.value = parseInt(bal.user.pvalues, 10);
    } catch (e) {
      console.error(e);
    }
  }
  const q = route.query;
  if (q.sessionId) {
    sessionId.value       = q.sessionId;
    sessionPassword.value = q.password || '';
    sessionCreated.value  = true;
    isPrivate.value       = !!q.password;
    isHost.value          = false;
    try {
      await joinCheckersSession(sessionId.value, sessionPassword.value, token);
      startPolling();
    } catch (err) {
      alert('Não foi possível entrar: ' + err.message);
      router.push('/CheckersMultiplayer');
    }
  }
});

// polling de status
const status = ref({});
let pollInterval = null;
async function pollStatus() {
  try {
    const st = await fetchCheckersSessionStatus(sessionId.value);
    status.value = st;
    players.value       = st.players;
    board.value         = st.board;
    currentPlayer.value = st.currentPlayerIndex;
    gameOver.value      = st.gameOver;
    winner.value        = st.winner;
    betAmount.value     = st.betAmount;
    gameStarted.value   = !!st.board;
    // atualiza saldo se gameOver
    if (st.gameOver) {
      const bal = await fetchBetBalance(token);
      userBalance.value = parseInt(bal.user.pvalues, 10);
    }
  } catch(e) {
    console.error(e);
  }
}

function startPolling() {
  pollInterval = setInterval(pollStatus, 1000);
}

onBeforeUnmount(() => {
  if (pollInterval) clearInterval(pollInterval);
});

// criar sala
async function createSession(publicGame) {
  try {
    const data = await createCheckersSession(
      { isPublic: publicGame, betAmount: betAmount.value },
      token
    );
    sessionLink.value      = data.link;
    const urlObj           = new URL(data.link);
    sessionId.value        = urlObj.searchParams.get('sessionId');
    sessionPassword.value  = data.password || '';
    sessionCreated.value   = true;
    isPrivate.value        = !publicGame;
    isHost.value           = true;
    startPolling();
  } catch (err) {
    alert('Erro ao criar sessão: ' + err.message);
  }
}

// clique na célula
function onCellClick(r, c) {
  if (!isMyTurn.value || gameOver.value) return;

  // converte coords de exibição para coords de modelo
  const model = flipBoard.value
    ? { r: 7 - r, c: 7 - c }
    : { r, c };

  // 1) Se ainda não selecionou peça, selecione só sua peça
  if (!selectedModel.value) {
    if (board.value[model.r][model.c]?.mid === userName.value) {
      selectedModel.value = model;
    }
    return;
  }

  // 2) Se já selecionou, esse click define o destino
  const from = selectedModel.value;
  const to   = model;
  doMove(from, to);
}


// executa ação de mover
async function doMove(from, to) {
  try {
    // manda ao servidor
    await actionCheckersSession(sessionId.value, from, to, token);

    // atualiza board e turno
    await pollStatus();

    // detecta captura (2 casas de distância)
    const isCapture = Math.abs(to.r - from.r) === 2;

    // Se foi captura E essa mesma peça ainda pode capturar de novo,
    // mantém selectedModel no novo ponto; senão, limpa seleção
    if (
      isCapture &&
      hasCaptureMovesLocal(
        board.value,
        to.r,
        to.c,
        players.value,
      )
    ) {
      selectedModel.value = to;
    } else {
      selectedModel.value = null;
    }
  } catch (err) {
    showError(err.message);
  }
}

// reiniciar / sair
async function startNewGame() {
  try {
    await restartCheckersSession(sessionId.value, token);
    await pollStatus();
  } catch (err) {
    alert('Erro ao reiniciar: ' + err.message);
  }
}

async function leaveRoom() {
 try {
   await leaveCheckersSession(sessionId.value, token);
 } catch (err) {
   // Se for 404 “Sessão não existe”, ignora
   if (!err.message.includes('Sessão não existe')) {
     console.error('Erro inesperado ao sair da sala:', err);
   }
 }
 // de qualquer forma, volta para a tela de lobby
 router.push('/CheckersMultiplayer');
}
</script>

<style scoped>
.checkers-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem;
}
.initial-screen {
  border: 1px solid #ccc;
  padding: 2rem;
  border-radius: 8px;
  text-align: center;
  width: 500px;
}
.bet-area {
  display: flex;
  gap: 1rem;
  align-items: center;
  margin-bottom: 1.5rem;
}
.balance {
  font-weight: bold;
}
.start-menu {
  display: flex;
  gap: 1rem;
}
.btn {
  padding: 0.75rem 1.5rem;
  background: #1976d2; color: #fff;
  border: none; border-radius: 4px;
  cursor: pointer;
}
.game-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}
.info-line {
  font-size: 1.25rem;
  margin: 0;
}
.board {
  border: 5px solid #333;
  margin-top: 1rem;
}
.row {
  display: flex;
}
.cell {
  width: 80px;
  height: 80px;
  box-sizing: border-box;
  border: 1px solid #555;
  position: relative;
}
.cell.dark {
  background: #769656;
}
.cell:not(.dark) {
  background: #eeeed2;
}
.cell.selected {
  outline: 3px solid yellow;
}
.piece {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  margin: auto;
  margin-top: 5px;
}
.piece.me {
  background: black;
}
.piece.opp {
  background: red;
}
.piece.king {
  box-shadow: inset 0 0 0 4px gold;
}
.host-controls {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 1rem;
}


/* container geral já centraliza o checkers-container */

.popup {
  position: fixed;
  /* centraliza no meio da tela */
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 1rem 1.5rem;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.3);
  z-index: 1000;
  text-align: center;
}

.error-popup {
  background: #ffd6d6;
  color: #a00;
  margin-top: 30%;
}

.victory-popup {
  background: #e0ffe0;
  color: #060;
  margin-top: 2rem;
}

.victory-popup .btn {
  margin-top: 1rem;
}

.abandon-popup {
  background: #fff4e5;
  color: #663c00;
}
</style>